import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
    ListGeneralAgreementsDocument,
    CreateGeneralAgreementDocument,
    UpdateGeneralAgreementDocument,
    DeleteGeneralAgreementDocument,
    SortOrder,
} from '~/gql/ucpw/graphql';
import { withRenderers } from '~/utils';
import { tables } from '~/pages/master-data/meta/data/generalAgreements.schema';
import { useDeleteWithConfirmation } from '~/hooks/useDeleteWithConfirmation';
import { useContextAndRefetchQueries } from '~/hooks/useContextAndRefetchQueries';
import { useModal } from '~/hooks/useModal';
import { usePermission } from '~/hooks/usePermission';

const LIMIT = 500;

export const useAgreements = () => {
    const { canEdit, canDelete } = usePermission('masterData.agreements');

    // ===============================================
    // LISTING
    // ===============================================

    const context = { clientName: 'ucpw' };
    const variables = {
        limit: 500,
        orderBy: [{ isStandard: SortOrder.Desc }, { bezeichnung: SortOrder.Asc }],
    };
    const { data, loading: listLoading } = useQuery(ListGeneralAgreementsDocument, {
        context,
        variables: {
            limit: LIMIT,
            orderBy: [{ isStandard: SortOrder.Desc }, { bezeichnung: SortOrder.Asc }],
        },
    });
    const agreements: any = data?.items?.items || [];

    const contextAndRefetchQueries = useContextAndRefetchQueries(
        ListGeneralAgreementsDocument,
        variables
    );

    // ===============================================
    // (C)R(U)D
    // ===============================================

    const [createMutation, { loading: createLoading }] = useMutation(
        CreateGeneralAgreementDocument,
        contextAndRefetchQueries
    );
    const [updateMutation, { loading: updateLoading }] = useMutation(
        UpdateGeneralAgreementDocument,
        contextAndRefetchQueries
    );
    const [deleteMutation, { loading: deleteLoading }] = useMutation(
        DeleteGeneralAgreementDocument,
        contextAndRefetchQueries
    );

    const { deleteWithConfirmation } = useDeleteWithConfirmation({
        deleteMutation,
        refetchQueries: contextAndRefetchQueries.refetchQueries,
        dependencies: [deleteMutation, contextAndRefetchQueries.refetchQueries],
    });

    const loading = listLoading || createLoading || updateLoading || deleteLoading;

    // ===============================================
    // MODAL
    // ===============================================

    const { onOpen, dispatch } = useModal();

    // ===============================================
    // CONTROLS
    // ===============================================

    const setGeneralAgreementsModal = () =>
        dispatch?.({
            type: 'setModal',
            data: {
                modal: 'GeneralAgreementsModal',
                props: { createMutation, updateMutation, loading },
            },
        });

    const onClick = React.useCallback(
        (row: any) => {
            setGeneralAgreementsModal();
            dispatch?.({ type: 'setRow', data: { row } });
            onOpen?.();
        },
        [dispatch, onOpen, createMutation, updateMutation, loading]
    );

    const controls: any = [
        canEdit && {
            title: 'Bearbeiten',
            props: { onClick },
        },
        canEdit && canDelete && 'divider',
        canDelete && {
            title: 'Löschen',
            props: {
                color: 'red.400',
                onClick: (row: any) => {
                    deleteWithConfirmation({ id: row?.original?.id });
                },
            },
        },
    ].filter(Boolean);

    const columns = React.useMemo(
        () => withRenderers(tables.agreements.columns, controls),
        [controls]
    );
    const hiddenColumns = tables.agreements.hiddenColumns;

    const onCreate = () => {
        setGeneralAgreementsModal();
        dispatch?.({ type: 'setRow', data: {} });
        onOpen?.();
    };

    const onImport = () => {
        dispatch?.({
            type: 'setModal',
            data: { modal: 'GeneralAgreementsCsvImportModal', props: {} },
        });
        dispatch?.({ type: 'setRow', data: {} });
        onOpen?.();
    };

    return {
        onCreate,
        onImport,
        data: agreements,
        hiddenColumns,
        columns,
        loading,
        pageSize: LIMIT,
        standard: agreements?.[0],
    };
};
